import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#F7F9FB]" data-testid="privacy-page">
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/" data-testid="privacy-back" className="text-[#6B7280] hover:text-[#1F3A5F]">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-lg font-bold text-[#1F3A5F]">Privacy Policy</h1>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 py-8 prose prose-sm text-[#2C2C2C]">
        <p className="text-xs text-[#6B7280]">Last updated: February 2026</p>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Overview</h2>
        <p>
          SayItBetter ("we", "us", "our") provides an AI-powered communication coaching service available via
          web and mobile app. We take your privacy seriously. This policy explains what we collect, how we use
          it, and your rights.
        </p>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Data we collect</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Account data:</strong> your email address, optional name, and an encrypted password hash.</li>
          <li><strong>Messages you analyze:</strong> the text you submit for analysis plus the AI's response,
            saved privately to your account so you can review your history.</li>
          <li><strong>Usage data:</strong> a session ID and counters to enforce daily free limits.</li>
          <li><strong>Email marketing (optional):</strong> if you opt in to early-access emails, we share your
            email with our email provider (MailerLite).</li>
        </ul>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">How we use your data</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>To deliver the core service: analyzing your messages and returning rewrite suggestions.</li>
          <li>To authenticate your account and keep your data private to you.</li>
          <li>To send password-reset and important account emails.</li>
          <li>To improve the quality of our AI guidance (aggregate, de-identified metrics only).</li>
        </ul>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Third-party services</h2>
        <p>
          To deliver our service we share limited data with trusted providers:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>OpenAI</strong> — the AI model that analyzes messages. We do not use your data to train
            public models; messages are sent for inference only.</li>
          <li><strong>MailerLite</strong> — email delivery for early-access and transactional emails.</li>
          <li><strong>MongoDB</strong> — encrypted database storage for your account and history.</li>
        </ul>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Data retention</h2>
        <p>
          We retain your account data for as long as your account is active. You can delete your account and
          all associated data at any time from the Settings screen or by visiting
          {" "}<Link to="/delete-account" className="text-[#2F5BFF] font-medium">sayitbetter.me/delete-account</Link>.
        </p>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Your rights</h2>
        <p>
          Depending on where you live, you may have rights under laws such as GDPR (EU) or CCPA (California),
          including the right to access, correct, export, or delete your data. To exercise any of these rights
          email us at <a href="mailto:hello@sayitbetter.me" className="text-[#2F5BFF] font-medium">hello@sayitbetter.me</a>.
        </p>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Children's privacy</h2>
        <p>
          SayItBetter is not intended for children under 13. We do not knowingly collect data from children.
          If we learn we have collected data from a child, we will delete it promptly.
        </p>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Security</h2>
        <p>
          Passwords are hashed with bcrypt. All traffic between your device and our servers is encrypted via
          HTTPS. We employ reasonable technical and organizational safeguards, but no method of transmission
          over the internet is 100% secure.
        </p>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Changes to this policy</h2>
        <p>
          We may update this policy from time to time. If we make material changes we'll notify you by email
          or in-app notice before the changes take effect.
        </p>

        <h2 className="text-xl font-bold text-[#1F3A5F] mt-6">Contact</h2>
        <p>
          Questions? Email <a href="mailto:hello@sayitbetter.me" className="text-[#2F5BFF] font-medium">hello@sayitbetter.me</a>.
        </p>
      </article>
    </div>
  );
}
